import { useEffect, useRef, useState } from 'react';

export type NavScrollInfo = { isScrolled: boolean; direction: 'up' | 'down' };

/**
 * Hook used by the NavbarPure component to react to scrolling events
 */
export function useNavScroll() {
  const spacerRef = useRef<HTMLDivElement>(null);

  const prevY = useRef(0);
  const prevDirection = useRef<NavScrollInfo['direction']>('up');

  const [scroll, setScroll] = useState<NavScrollInfo>({
    isScrolled: false,
    direction: 'up',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      const isScrolled = !entry.isIntersecting;
      setScroll((prev) =>
        prev.isScrolled === isScrolled ? prev : { ...prev, isScrolled }
      );
    });

    const onScroll = () => {
      const y = document.scrollingElement?.scrollTop ?? 0;
      const direction = y > prevY.current ? 'down' : 'up';
      prevY.current = y;

      if (prevDirection.current !== direction) {
        prevDirection.current = direction;

        setScroll((prev) =>
          prev.direction === direction ? prev : { ...prev, direction }
        );
      }
    };

    document.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });

    if (spacerRef.current) {
      observer.observe(spacerRef.current);
    }

    return () => {
      observer.disconnect();
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  return { scroll, spacerRef };
}
